import usePageTitle from "../utils/usePageTitle";
import React, { useEffect, useMemo, useState } from "react";
import { Download, Mail, PackageOpen } from "lucide-react";
import { Container, ErrorBlockGeneric } from "common-tailwind-components";
import { useTranslation } from "react-i18next";

import {
  get_clank_release_asset_download_url,
  get_clank_releases,
  type ForgejoRelease,
  type ForgejoReleaseAsset,
} from "../api";
import { IS_COMPANY_ROUTER_FRONTEND, IS_STATIC_DEMO_FRONTEND } from "../globals";

type DownloadOs = "windows" | "macos" | "linux";

type DownloadAsset = ForgejoReleaseAsset & {
  os: DownloadOs;
  releaseName: string;
  releaseTag: string;
  releaseCreatedAt: string | null;
};

type PlatformLogoProps = {
  size?: number;
};

const WindowsLogo = ({ size = 18 }: PlatformLogoProps) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size} className="shrink-0">
    <path fill="#00A4EF" d="M2.5 4.4 10.7 3v8.1H2.5V4.4Z" />
    <path fill="#7FBA00" d="m12 2.8 9.5-1.6v9.9H12V2.8Z" />
    <path fill="#F25022" d="M2.5 12.4h8.2v8.2l-8.2-1.4v-6.8Z" />
    <path fill="#FFB900" d="M12 12.4h9.5v10.4L12 21.2v-8.8Z" />
  </svg>
);

const MacLogo = ({ size = 18 }: PlatformLogoProps) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size} className="shrink-0">
    <path
      fill="currentColor"
      d="M15.5 2.2c.2 1.5-.5 3-1.4 4-.9 1-2.4 1.8-3.8 1.7-.2-1.4.5-2.9 1.4-3.9.9-1.1 2.5-1.9 3.8-1.8Z"
    />
    <path
      fill="currentColor"
      d="M20.2 17.4c-.5 1.2-.8 1.8-1.5 2.9-1 1.5-2.4 3.4-4.1 3.4-1.5 0-1.9-1-3.9-1s-2.5 1-3.9 1c-1.7 0-3-1.7-4-3.2C.1 16.4 0 11.7 1.7 9.4c1.2-1.6 3-2.6 4.7-2.6 1.8 0 2.9 1 4.3 1 1.4 0 2.3-1 4.3-1 1.5 0 3.1.8 4.3 2.2-3.8 2.1-3.2 7.5.9 8.4Z"
    />
  </svg>
);

const LinuxLogo = ({ size = 18 }: PlatformLogoProps) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size} className="shrink-0">
    <path
      fill="#111827"
      d="M12 1.8c-2.9 0-5 2.6-5 6.2 0 1-.2 1.8-.7 2.6-1.1 1.8-2.1 3.7-2.1 6.2 0 3.3 3.1 5.4 7.8 5.4s7.8-2.1 7.8-5.4c0-2.5-1-4.4-2.1-6.2-.5-.8-.7-1.6-.7-2.6 0-3.6-2.1-6.2-5-6.2Z"
    />
    <path
      fill="#F8FAFC"
      d="M8.1 15.9c0-3.1 1.7-5.7 3.9-5.7s3.9 2.6 3.9 5.7c0 2.7-1.4 4.4-3.9 4.4s-3.9-1.7-3.9-4.4Z"
    />
    <path
      fill="#FBBF24"
      d="M5 19.3c1.1-.9 2.5-.8 3.6.1-.8 1.1-2.5 1.7-4.1 1.3-.6-.2-.5-.9.5-1.4Z"
    />
    <path
      fill="#FBBF24"
      d="M19 19.3c-1.1-.9-2.5-.8-3.6.1.8 1.1 2.5 1.7 4.1 1.3.6-.2.5-.9-.5-1.4Z"
    />
    <circle cx="9.8" cy="7.2" r="1" fill="#F8FAFC" />
    <circle cx="14.2" cy="7.2" r="1" fill="#F8FAFC" />
    <circle cx="10.1" cy="7.3" r=".35" fill="#111827" />
    <circle cx="13.9" cy="7.3" r=".35" fill="#111827" />
    <path fill="#FBBF24" d="m12 8.4-1.3 1.2c.7.5 1.9.5 2.6 0L12 8.4Z" />
  </svg>
);

const osChoices: Array<{ id: DownloadOs; icon: React.ReactNode }> = [
  { id: "windows", icon: <WindowsLogo /> },
  { id: "macos", icon: <MacLogo /> },
  { id: "linux", icon: <LinuxLogo /> },
];

const classifyAssetOs = (assetName: string): DownloadOs | null => {
  const name = assetName.toLowerCase();
  if (
    name.includes("windows") ||
    name.includes("win") ||
    name.endsWith(".exe") ||
    name.endsWith(".msi")
  ) {
    return "windows";
  }
  if (
    name.includes("macos") ||
    name.includes("darwin") ||
    name.endsWith(".dmg") ||
    name.endsWith(".pkg")
  ) {
    return "macos";
  }
  if (
    name.includes("linux") ||
    name.endsWith(".appimage") ||
    name.endsWith(".deb") ||
    name.endsWith(".rpm") ||
    name.endsWith(".tar.gz")
  ) {
    return "linux";
  }
  return null;
};

const buildDownloadAssets = (releases: ForgejoRelease[]): DownloadAsset[] =>
  releases.flatMap(release =>
    (release.assets ?? []).flatMap(asset => {
      const os = classifyAssetOs(asset.name);
      if (!os || !asset.browser_download_url) {
        return [];
      }
      return [
        {
          ...asset,
          os,
          releaseName: release.name || release.tag_name,
          releaseTag: release.tag_name,
          releaseCreatedAt: release.created_at ?? null,
        },
      ];
    }),
  );

const guessPreferredOs = (): DownloadOs => {
  const platformHint = navigator.platform || "";
  const userAgent = navigator.userAgent || "";
  const haystack = `${platformHint} ${userAgent}`.toLowerCase();
  if (haystack.includes("mac")) return "macos";
  if (haystack.includes("linux") || haystack.includes("x11")) return "linux";
  return "windows";
};

const formatSize = (size: number): string => {
  if (!Number.isFinite(size) || size <= 0) return "";
  return `${Math.round(size / 1024 / 1024).toLocaleString()} MB`;
};

const formatDate = (value: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
};

const StaticDemoDownloadsNotice = () => {
  const { t } = useTranslation();

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-text">{t("downloads_page")}</h1>
        <p className="mt-2 max-w-2xl text-sm text-text-dim">{t("downloads_page_description")}</p>
      </div>

      <div className="rounded-xl border border-warning/40 bg-warning/10 p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-text">
              <PackageOpen size={18} />
              {t("downloads_registered_users_only_title")}
            </div>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-text-dim">
              {t("downloads_registered_users_only_message")}
            </p>
          </div>
          <a
            href="mailto:info@platonicresearch.com?subject=Platonic%20onboarding"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-on-accent transition hover:opacity-90"
          >
            <Mail size={16} />
            {t("contact_us_for_onboarding")}
          </a>
        </div>
      </div>
    </div>
  );
};

const Downloads = () => {
  const { t } = useTranslation();
  usePageTitle(t("downloads_page"));
  const [releases, setReleases] = useState<ForgejoRelease[]>([]);
  const [selectedOs, setSelectedOs] = useState<DownloadOs>(() => {
    const stored = window.localStorage.getItem("preferred-download-os") as DownloadOs | null;
    return stored && osChoices.some(choice => choice.id === stored) ? stored : guessPreferredOs();
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!IS_COMPANY_ROUTER_FRONTEND) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    get_clank_releases()
      .then(data => {
        if (!cancelled) {
          setReleases(data);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t("downloads_load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const assets = useMemo(() => buildDownloadAssets(releases), [releases]);
  const assetsByOs = useMemo(() => {
    return osChoices.reduce<Record<DownloadOs, DownloadAsset[]>>(
      (acc, choice) => {
        acc[choice.id] = assets.filter(
          asset => asset.os === choice.id && asset.name.match(/platonic/i),
        );
        return acc;
      },
      { windows: [], macos: [], linux: [] },
    );
  }, [assets]);
  const visibleAssets = assetsByOs[selectedOs];

  if (IS_STATIC_DEMO_FRONTEND) {
    return (
      <Container>
        <div className="px-6 pb-16 pt-7">
          <StaticDemoDownloadsNotice />
        </div>
      </Container>
    );
  }

  if (!IS_COMPANY_ROUTER_FRONTEND) {
    return (
      <Container>
        <div className="px-6 pb-16 pt-7">
          <div className="rounded-xl border border-outline bg-surface p-6 text-sm text-text-dim">
            {t("page_not_found")}
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="px-6 pb-16 pt-7">
        <div className="max-w-5xl">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-text">{t("downloads_page")}</h1>
            <p className="mt-2 max-w-2xl text-sm text-text-dim">
              {t("downloads_page_description")}
            </p>
          </div>

          <div
            className="mb-5 flex flex-wrap gap-2"
            role="tablist"
            aria-label={t("operating_systems")}
          >
            {osChoices.map(choice => {
              const active = choice.id === selectedOs;
              return (
                <button
                  key={choice.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => {
                    setSelectedOs(choice.id);
                    window.localStorage.setItem("preferred-download-os", choice.id);
                  }}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                    active
                      ? "border-info/40 bg-info/15 text-info-strong"
                      : "border-outline bg-surface text-text-dim hover:bg-muted-surface hover:text-text"
                  }`}
                >
                  {choice.icon}
                  {t(choice.id)}
                  <span className="rounded-full bg-muted-surface px-2 py-0.5 text-xs">
                    {assetsByOs[choice.id].length}
                  </span>
                </button>
              );
            })}
          </div>

          {error && <ErrorBlockGeneric error={error} />}

          <div className="space-y-3">
            {loading ? (
              <div className="rounded-xl border border-outline bg-surface p-5 text-sm text-text-dim">
                {t("loading_ellipsis")}
              </div>
            ) : visibleAssets.length === 0 ? (
              <div className="rounded-xl border border-outline bg-surface p-5 text-sm text-text-dim">
                {t("no_downloads_available_for_os", { os: t(selectedOs) })}
              </div>
            ) : (
              visibleAssets.map(asset => {
                const released = formatDate(asset.releaseCreatedAt);
                const size = formatSize(asset.size);
                const details = [asset.releaseTag, released && `${t("released")} ${released}`, size]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <article
                    key={`${asset.releaseTag}-${asset.name}`}
                    className="flex flex-col gap-4 rounded-xl border border-outline bg-surface p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-info-strong">
                        <PackageOpen size={14} />
                        {asset.releaseName}
                      </div>
                      <h2 className="mt-2 truncate text-lg font-semibold text-text">
                        {asset.name}
                      </h2>
                      {details && <p className="mt-1 text-sm text-text-dim">{details}</p>}
                    </div>
                    <a
                      href={get_clank_release_asset_download_url(asset.browser_download_url)}
                      download={asset.name}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-on-accent transition hover:opacity-90"
                    >
                      <Download size={16} />
                      {t("download")}
                    </a>
                  </article>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};

export default Downloads;
